import { useId, useState } from 'react';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULE,
  passwordProgress,
} from '../../credential/password-rule';

/**
 * A password being chosen (docs/DECISIONS.md ZD-118 §2).
 *
 * The rule is on screen before anything is typed, phrased as a rule; the
 * count beside it is the same number the API refuses on; and the show
 * control stands in for a confirm field (`password-rule.ts` says why).
 * `autocomplete="new-password"` lets a manager offer and store one.
 */
export function PasswordField({
  value,
  onChange,
  label = 'Choose a password',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}): React.JSX.Element {
  const id = useId();
  const [shown, setShown] = useState(false);
  const progress = passwordProgress(value);

  return (
    <div className="cred__field">
      <label className="field" htmlFor={id}>
        {label}
      </label>
      <div className="cred__password">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          value={value}
          autoComplete="new-password"
          spellCheck={false}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
        <button
          type="button"
          className="cred__show"
          aria-pressed={shown}
          onClick={() => {
            setShown((current) => !current);
          }}
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>
      <p className="cred__rule">
        <span>{PASSWORD_RULE}</span>{' '}
        <output className="cred__count" data-meets={progress.meets} aria-live="polite">
          {progress.meets
            ? `${String(progress.length)} characters`
            : `${String(progress.length)} of ${String(PASSWORD_MIN_LENGTH)}`}
        </output>
      </p>
    </div>
  );
}
